/**
 * Firestore クォータ超過エラーを検出して読み取りを一時停止するサーキットブレーカー。
 *
 * 状態遷移:
 *   CLOSED（正常）→ クォータエラー 3 回連続 → OPEN（遮断）
 *   OPEN → 60 秒経過 → HALF_OPEN（試行許可）
 *   HALF_OPEN → 成功 → CLOSED
 *   HALF_OPEN → 失敗 → OPEN（リセット）
 */

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

const FAILURE_THRESHOLD = 3;
const RECOVERY_TIMEOUT_MS = 60_000; // 60 秒

/** Firestore の resource-exhausted エラーかどうか判定 */
const isQuotaError = (error: unknown): boolean => {
  if (!error) return false;
  const code = (error as any)?.code as string | undefined;
  const message = String((error as any)?.message ?? '');
  return (
    code === 'resource-exhausted' ||
    message.toLowerCase().includes('quota exceeded') ||
    message.toLowerCase().includes('resource-exhausted')
  );
};

class CircuitBreaker {
  private state: CircuitState = 'CLOSED';
  private failureCount = 0;
  private lastFailureTime = 0;

  /** 遮断中（OPEN）なら true を返す。HALF_OPEN への遷移も内部で行う。 */
  isOpen(): boolean {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime >= RECOVERY_TIMEOUT_MS) {
        this.state = 'HALF_OPEN';
        console.log('[CircuitBreaker] → HALF_OPEN: attempting recovery');
        return false;
      }
      return true;
    }
    return false;
  }

  /** 読み取り成功時に呼ぶ */
  recordSuccess(): void {
    if (this.state !== 'CLOSED') {
      console.log('[CircuitBreaker] → CLOSED: Firestore reads resumed');
    }
    this.failureCount = 0;
    this.state = 'CLOSED';
  }

  /** 読み取り失敗時に呼ぶ（クォータエラー以外は無視） */
  recordFailure(error: unknown): void {
    if (!isQuotaError(error)) return;

    this.failureCount++;
    this.lastFailureTime = Date.now();

    if (this.state === 'HALF_OPEN' || this.failureCount >= FAILURE_THRESHOLD) {
      this.state = 'OPEN';
      console.error(
        `[CircuitBreaker] → OPEN: Firestore reads suspended for ${RECOVERY_TIMEOUT_MS / 1000}s`
      );
    }
  }

  getState(): CircuitState {
    return this.state;
  }
}

export const circuitBreaker = new CircuitBreaker();
